import { createTheme } from '@mui/material/styles';

/**
 * MetLife Brand Theme
 * 
 * Colors based on MetLife brand guidelines:
 * - Primary: MetLife Blue (#0077C8)
 * - Secondary: MetLife Green (#00A651)
 * - Dark backgrounds with professional appearance
 */
const theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#0077C8', // MetLife Blue
            light: '#3399D6',
            dark: '#005A9C',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#00A651', // MetLife Green
            light: '#33B873',
            dark: '#007A3D',
            contrastText: '#ffffff',
        },
        success: {
            main: '#00A651',
            light: '#33B873',
            dark: '#007A3D',
        },
        warning: {
            main: '#F7941D',
            light: '#F9A94D',
            dark: '#C77517',
        },
        error: {
            main: '#C8102E',
            light: '#D64058',
            dark: '#9C0C24',
        },
        background: {
            default: '#F5F7FA',
            paper: '#FFFFFF',
        },
        text: {
            primary: '#1A1A1A',
            secondary: '#666666',
        },
        divider: 'rgba(0, 0, 0, 0.08)',
        // Custom MetLife colors
        metlife: {
            blue: '#0077C8',
            green: '#00A651',
            darkBlue: '#00264D',
            lightGray: '#F5F7FA',
            mediumGray: '#E5E5E5',
        },
    },
    typography: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        h1: {
            fontSize: '2.5rem',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: '#1A1A1A',
        },
        h2: {
            fontSize: '2rem',
            fontWeight: 600,
            letterSpacing: '-0.01em',
        },
        h3: {
            fontSize: '1.5rem',
            fontWeight: 600,
        },
        h4: {
            fontSize: '1.25rem',
            fontWeight: 600,
        },
        h5: {
            fontSize: '1rem',
            fontWeight: 600,
        },
        h6: {
            fontSize: '0.875rem',
            fontWeight: 600,
        },
        body1: {
            fontSize: '1rem',
            lineHeight: 1.6,
        },
        body2: {
            fontSize: '0.875rem',
            lineHeight: 1.5,
        },
        button: {
            textTransform: 'none',
            fontWeight: 500,
        },
    },
    shape: {
        borderRadius: 8,
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    padding: '10px 24px',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                },
                contained: {
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: '0 2px 8px rgba(0, 119, 200, 0.25)',
                    },
                    '&.Mui-disabled': {
                        backgroundColor: '#e0e0e0',
                        color: '#9e9e9e',
                        boxShadow: 'none',
                    },
                },
                containedPrimary: {
                    background: 'linear-gradient(135deg, #0077C8 0%, #005A9C 100%)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #005A9C 0%, #004A7F 100%)',
                    },
                    '&.Mui-disabled': {
                        background: '#e0e0e0',
                        color: '#9e9e9e',
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                    border: '1px solid rgba(0, 0, 0, 0.06)',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    fontWeight: 500,
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 6,
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    minHeight: 48,
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#00264D', // Dark blue sidebar
                    color: '#ffffff',
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    margin: '4px 8px',
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        },
                    },
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    },
                },
            },
        },
    },
});

export default theme;
